import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { UserResult } from "@/types/search";

interface RecommendedUsers {
  mutualFollows: UserResult[];
  similarReaders: UserResult[];
  trendingUsers: UserResult[];
}

export const useRecommendedUsers = () => {
  return useQuery({
    queryKey: ['recommendedUsers'],
    queryFn: async (): Promise<RecommendedUsers> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('recommend-users');
      
      if (error) throw error;

      // Fetch current follow relationships
      const allUserIds = [
        ...(data.mutualFollows || []),
        ...(data.similarReaders || []),
        ...(data.trendingUsers || [])
      ].map((u: any) => u.id);

      const { data: follows } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', user.id)
        .in('following_id', allUserIds);

      const followingIds = new Set(follows?.map(f => f.following_id) || []);

      // Add isFollowing property to each user
      const addFollowStatus = (users: UserResult[]) => 
        users.map(u => ({ ...u, isFollowing: followingIds.has(u.id) }));

      return {
        mutualFollows: addFollowStatus(data.mutualFollows || []),
        similarReaders: addFollowStatus(data.similarReaders || []),
        trendingUsers: addFollowStatus(data.trendingUsers || [])
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
